/**
 * MetricMatrix — Matriz de bloques compacta para tarjetas KPI.
 *
 * QUÉ HACE: dibuja una matriz de bloques que **ocupa todo el ancho y alto** del
 *   cuerpo de la tarjeta. Cada fila es una categoría (con su color) y se divide
 *   en tantos bloques como columnas; la fila pinta `value` bloques sólidos con
 *   su color y el resto queda como carril tenue. Incluye ejes etiquetados como
 *   los heatmaps grandes, pero en formato mini: **título del eje** arriba,
 *   etiquetas de fila a la izquierda y numeración de columnas (`#1…#n`) abajo.
 *   La usa la prop `matrix` de MetricCard (Alertas Activas: una fila por
 *   severidad y un bloque por alerta).
 * QUÉ INVOCA: React (Fragment) y `useReducedMotion`.
 * QUÉ EXPORTA: MetricMatrix, MetricMatrixProps.
 * QUÉ NO DEBE HACER: no calcula los datos ni accede a almacenamiento.
 */

import type { CSSProperties } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { staggerDelay } from "@/lib/animation/motion";

/** Fila de la matriz (categoría con su color y nº de bloques activos). */
interface MetricMatrixRow {
  label: string;
  value: number;
  /** Color de los bloques activos de la fila (hex, p. ej. "#ef4444"). */
  color: string;
}

export interface MetricMatrixProps {
  rows: MetricMatrixRow[];
  /** Título del eje superior (p. ej. "Cantidad de alertas (1 a 3)"). */
  axisTitle?: string;
  /** Nº de columnas; si se omite, el mayor `value`. */
  columns?: number;
  className?: string;
}

export const MetricMatrix = ({
  rows,
  axisTitle,
  columns,
  className = "",
}: MetricMatrixProps) => {
  const reducedMotion = useReducedMotion();
  const cols =
    columns ?? rows.reduce((max, row) => Math.max(max, row.value), 0);
  const colIndexes = Array.from({ length: cols }, (_, index) => index);

  return (
    <div
      className={`flex h-full w-full min-h-0 flex-1 flex-col justify-center gap-1.5 ${className}`}
      role="img"
      aria-label={rows.map((row) => `${row.label} ${row.value}`).join(", ")}
    >
      {axisTitle && (
        <p className="shrink-0 text-center text-[8px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500 sm:text-[9px]">
          {axisTitle}
        </p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-1">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex min-h-0 flex-1 items-stretch gap-2"
          >
            <span className="w-12 shrink-0 self-center truncate text-right text-[9px] font-semibold text-gray-500 sm:w-14 sm:text-[10px] dark:text-slate-400">
              {row.label}
            </span>
            <div className="flex min-h-0 min-w-0 flex-1 gap-1">
              {colIndexes.map((col) => {
                const active = col < row.value;
                return (
                  <span
                    key={`${row.label}-${col}`}
                    className={`min-h-0 flex-1 rounded-[3px] ${
                      active ? "" : "bg-gray-100 dark:bg-slate-700/40"
                    }${active && !reducedMotion ? " animate-chart-pop" : ""}`}
                    style={
                      active
                        ? ({
                            backgroundColor: row.color,
                            animationDelay: reducedMotion
                              ? undefined
                              : staggerDelay(col),
                          } as CSSProperties)
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span className="w-12 shrink-0 sm:w-14" />
        <div className="flex min-w-0 flex-1 gap-1">
          {colIndexes.map((col) => (
            <span
              key={`label-${col}`}
              className="flex-1 text-center text-[8px] font-medium tabular-nums text-gray-400 dark:text-slate-500"
            >
              #{col + 1}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
